'use strict';
/* ==========================================================================
   levels/poster.js — step "poster" (orientation any): FIX THE POSTER (DESIGN.md §5.2). Judge science-fair posters.

   An attempt = 6 posters from the pool `poster` (content/bank.poster.json through Nos.pool → core/bank.js: draw.n = 6,
   draw.require = ["legit"] so one honest poster, draw.unique = "cheat" so no cheat twice), served easiest first
   (Nos.byLevel). Every poster is built by the model: Nos.poster(item) → {title, claims: [{text, bad, verdict}], answer:
   {kind: 'claim' | 'none', index}, fix}. Nothing here decides what is true: `answer` and each claim's `verdict` (Nos.judge)
   do; the "checks out" notes are the model's own kid lines (Nos.STATEMENTS / ITEMS / EVIDENCE), never the author's text.

   A poster is a TRI-FOLD board standing on a science-fair table in the gym: two folded side flaps (little WHAT / WHY
   cards, a photo, a ribbon) and the middle panel with the title band and 3–4 claims (A–D), each up to two lines.
   Per poster:  drop (the board slides down onto the table)
             →  spot: tap the wrong claim, or "All correct". A wrongly accused claim gets a pencil tick and why it checks
                out; 3 misses and Dr. Voss circles the mistake herself (or calls the poster all correct).
             →  q: the item's question ("What's the fix?"), its 3 options shuffled by the bank as answer cards (up to two
                lines each); a wrong pick is crossed out with its `why` (Bank.why), pick again
             →  fix: Dr. Voss's red grease pencil strikes the wrong claim line by line, the claims below slide down and
                she writes the right option in its place (red hand lettering, a caret); her reason (the item's `fix`)
                is on her card. The all-correct poster gets green ticks and an ALL CORRECT stamp.
   Stars: right spot first try 1 + right option first try 1 (legit: "All correct" first try 1 + the question 1). of = 12,
   pass = pool.passStars (8). Game.progress.scores.poster = {best, last, of}; first answers under
   Game.progress.answers['poster/<key>/spot'] and ['poster/<key>']; Bank.finish at the end; the stamp (Game.complete)
   through "Collect your stamp" on a pass (a pass that leaves without collecting keeps it). Resume: Game.progress.bank.
   poster.run = {i, ev, res} (the current poster's taps and picks are replayed after a reload).

   Portrait phone (Dock.active): the poster is the picture on the stage; the claims, "All correct", the options and
   Dr. Voss's card are dock widgets.
   Keys: A–D = claim, 0 = All correct, 1–3 = option, Enter / Space = next / start (every key has an on-screen control).

   QA hooks: sc.phase ('intro' | 'drop' | 'spot' | 'q' | 'fix' | 'end'), sc.poster (the Nos.poster result), sc.inst,
   sc.answer() ({kind: 'claim' | 'legit', index}), sc.tapClaim(i), sc.legit(), sc.choose(i), sc.next(), sc.begin(fresh),
   sc.collect(), sc.done(), sc.finishStub() (= done); sc.bi, sc.n, sc.seed, sc.attempt, sc.resumed, sc.results, sc.stars,
   sc.misses, sc.picks; Levels.poster.solution ({kind, index, option}), Levels.poster.zones() ({claims} tap rects on the
   canvas), Levels.poster.preview(id) (swap the current poster for a pool item: screenshots of the longest claims),
   Levels.poster.fit() (the current layout's overflow report).
   ========================================================================== */
(function () {
  const STEP = 'poster', TITLE = 'Fix the Poster';
  const MAX_MISSES = 3, DROP = 0.8, NEXT_GUARD = 0.3;
  const pool = () => Bank.pool(STEP) || Bank.load(Nos.pool(Game.content.bank_poster));
  const F = () => window.Fair;
  const SLAB = "'Alfa Slab One'", SERIF = "'Playfair Display'", HAND = "'Caveat Brush'";
  const PENCIL = '#5b6475', GREEN = '#3d7a3a', FLAP = '#e8dcc0';
  const LETTER = ['A', 'B', 'C', 'D'];
  const SHORT = {
    legit: 'all correct', graduates: 'a theory "graduating" into a law', proven: 'laws called proven and unchanging',
    educatedGuess: 'a hypothesis called an educated guess', oneScientist: "a theory called one scientist's idea",
    swapJob: 'the jobs swapped (what and why)', whim: 'theories thrown out on a whim', alwaysMath: 'every law called an equation',
    mislabel: 'a law or a theory labeled the wrong kind',
  };
  const SMALLW = new Set(['of', 'the', 'a', 'an', 'and', 'or', 'to', 'in', 'on', 'for', 'by', 'at']);
  const titleCase = s => String(s).toLowerCase().split(' ').map((w, i) => (i && SMALLW.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(' ');
  const Rn = v => Math.max(1, Math.round(v));
  const R10 = v => Math.max(10, Math.round(v / 10) * 10);
  const measure = (s, font) => { g.save(); g.font = font; const w = g.measureText(s).width; g.restore(); return w; };
  const lerpN = (a, b, k) => a + (b - a) * k;
  const cap = s => s.charAt(0).toUpperCase() + s.slice(1);

  /* ---------------- the model's words: why a TRUE claim checks out ---------------- */
  function checksOut(c) {
    const v = c.verdict, N = window.Nos;
    if (v.kind === 'statement' && N.STATEMENTS[v.id]) return N.STATEMENTS[v.id].kid;
    if ((v.kind === 'kind' || v.kind === 'job') && N.ITEMS[v.item]) {
      const it = N.ITEMS[v.item];
      return `${cap(it.name)} ${it.job === 'why' ? 'explains why' : 'describes what happens'}: it's a ${it.kind}.`;
    }
    if (v.kind === 'evidence' && N.EVIDENCE[v.e] && N.ITEMS[v.item]) return `${N.EVIDENCE[v.e].text}: real evidence for ${N.ITEMS[v.item].name}.`;
    return 'The facts back it up.';
  }

  /* ---------------- layout (mode: 'm' mouse / desktop, 't' touch landscape, 'd' the portrait stage picture) ----------------
     The rewrite (the right option, written in under the struck claim) is part of the fit, so the fix never overflows;
     the rows are laid out WITHOUT it and the rows under the wrong claim slide down by rw.h during the fix. */
  function posterLay(P, mode, rewrite) {
    const d = mode === 'd', t = mode === 't';
    const PX = d ? 30 : 36, PW = d ? 1860 : 1014;
    const PY = d ? 118 : t ? 226 : 194, bottom = d ? 1046 : 1030, PH = bottom - PY;
    const flap = Math.round(PW * (d ? 0.07 : 0.1)), midL = PX + flap, midW = PW - 2 * flap, cx = PX + PW / 2;
    const bandH = d ? 100 : 78, bandY = PY + 16 + bandH / 2, titleMax = d ? 62 : 46;
    const inset = d ? 40 : 30;
    const colX = midL + inset, colR = midL + midW - inset, colW = colR - colX;
    const colTop = PY + 16 + bandH + (d ? 30 : 22), colBot = bottom - (d ? 28 : 22);
    const bad = P.answer.kind === 'claim' ? P.answer.index : -1;
    const base = d ? 56 : Math.round(Math.max(44, UI.text(36))), min = d ? 30 : 20;
    let C = null;
    for (let fs = base; fs >= min; fs -= 2) {
      const lh = Math.round(fs * 1.22), bw = Math.round(fs * 1.2), pad = Math.round(fs * 0.38), gap = Math.round(fs * 0.3), rowMin = d ? 0 : UI.tap(64);
      const font = `${fs}px ${SERIF}`, textX = colX + bw + 20, textW = colR - textX;
      let y = 0;
      const rows = P.claims.map((c, i) => {
        const lines = UI._wrap(c.text, textW, font);
        const h = Math.max(rowMin, lines.length * lh + pad * 2);
        const r = { i, top: y, h, tpad: (h - lines.length * lh) / 2, lines, lineW: lines.map(l => measure(l, font)) };
        y += h + gap;
        return r;
      });
      const total = y - gap;
      const rwFs = Math.round(fs * 1.12), rwFont = `${rwFs}px ${HAND}`, rwLh = Math.round(rwFs * 1.06);
      const rwLines = bad >= 0 && rewrite ? UI._wrap(rewrite, textW - 6, rwFont) : [];
      const rwH = rwLines.length ? rwLines.length * rwLh + Math.round(fs * 0.45) : 0;
      const room = colBot - colTop - total - rwH;
      C = { fs, lh, bw, pad, gap, font, textX, textW, rows, total, rw: { fs: rwFs, font: rwFont, lh: rwLh, lines: rwLines, h: rwH } };
      if (room >= 0 && (rows.every(r => r.lines.length <= 2) || fs - 2 < min)) {
        const off = Math.round(Math.min(room / 3, fs * 1.6));
        rows.forEach(r => { r.top += colTop + off; });
        C.over = 0;
        break;
      }
      rows.forEach(r => { r.top += colTop; });
      C.over = -room;
    }
    return { mode, PX, PW, PY, PH, bottom, flap, midL, midW, cx, bandH, bandY, titleMax, colX, colR, colW, colTop, colBot, bad, ...C };
  }

  class Poster extends Scene {
    get pool() { return pool(); }
    get n() { return this.instances ? this.instances.length : (this.pool.draw.n || 6); }
    get of() { return this.n * 2; }
    get passStars() { const j = this.pool.json || {}; return Math.min(this.of, j.passStars || Math.ceil(this.of * 2 / 3)); }
    get inst() { return this.instances ? this.instances[this.bi] : null; }
    get poster() { return this.P || null; }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.stars : 0), 0); }
    get solution() {
      const P = this.P, q = this.inst;
      if (!P) return null;
      const a = P.answer;
      return { kind: a.kind === 'none' ? 'legit' : a.kind, index: a.index, option: q.answerIndex, optionText: q.options[q.answerIndex] };
    }
    answer() { const s = this.solution; return s ? { kind: s.kind, index: s.index } : null; }

    enter() {
      this.phase = 'intro'; this.instances = null; this.P = null; this.bi = 0; this.results = []; this.passed = false; this.collected = false;
      this.lays = new Map();
      this.sfx('paper', { gain: 0.5 });
      const st = Game.progress.bank && Game.progress.bank[STEP];
      if (st && st.open && st.run && st.pool === STEP) this.begin();              // an attempt in progress: straight back in
    }
    exit() { this.bankStamp(); }
    back() { Game.replace(new Levels.map()); }

    /* ---- the attempt ---- */
    begin(fresh = false) {
      this.bankStamp();
      const S = Game.progress.bank || {}, prev = S[STEP] && S[STEP].open ? S[STEP].run : null;
      const a = Bank.attempt(STEP, pool(), { fresh });
      this.instances = Nos.byLevel(a.instances);                                    // easiest first (stable)
      this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed;
      this.results = []; this.passed = false; this.collected = false;
      if (a.resumed && prev && this.restore(prev)) return;
      this.startPoster(0);
    }
    restore(run) {
      try {
        if (!(run.i >= 0 && run.i < this.instances.length)) return false;
        this.results = (run.res || []).slice(0, run.i);
        this.setup(run.i);
        this.boardAt = Game.time - DROP - 2; this.phase = 'spot';
        this.quiet = true;
        for (const [k, v] of run.ev || []) {
          if (k === 'c') this.tapClaim(v); else if (k === 'l') this.legit(); else if (k === 'o') this.choose(v);
        }
        this.quiet = false;
        // replayed marks are already drawn (no re-animation after a reload)
        const past = Game.time - 5;
        for (const m of Object.values(this.marks)) m.at = past;
        if (this.say) this.say.at = past;
        if (this.foundAt != null) { this.foundAt = past; this.qAt = past + 0.5; }
        if (this.pickedAt != null) this.pickedAt = past;
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[poster] could not resume: ' + e.message); return false; }
    }
    setup(i) {
      this.bi = i; this.P = Nos.poster(this.instances[i].item); this.lays.clear();
      this.ev = []; this.misses = 0; this.marks = {};
      this.found = null; this.revealed = false; this.picks = []; this.say = null; this.spotStar = 0;
      this.boardAt = Game.time; this.foundAt = this.qAt = this.fixAt = this.pickedAt = null;
      this._sc = this._qc = this._fc = null;
    }
    startPoster(i) {
      this.setup(i); this.phase = 'drop';
      this.save();
      this.sfx('whoosh', { gain: 0.4 });
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end') S.run = { i: this.bi, ev: this.ev.slice(), res: this.results.slice() };
      Game.progress.save();
    }
    sfx(name, o) { if (!this.quiet && (!Audio.has || Audio.has(name))) Audio.play(name, o); }
    spotting() { if (this.phase === 'drop') this.phase = 'spot'; return this.phase === 'spot' && !!this.P; }
    spotKey() { return STEP + '/' + this.inst.key + '/spot'; }

    /* ---- accuse a claim, or call it all correct ---- */
    tapClaim(i) {
      const P = this.P;
      if (!this.spotting() || !(i >= 0 && i < P.claims.length) || this.marks[i]) return false;
      this.ev.push(['c', i]);
      Game.progress.answer(this.spotKey(), P.claims[i].text);                      // first try only
      if (P.answer.kind === 'claim' && P.answer.index === i) { this.foundIt(false); this.save(); return true; }
      this.marks[i] = { at: Game.time };
      this.miss(`Claim ${LETTER[i]} checks out. ${checksOut(P.claims[i])}`);
      this.save();
      return true;
    }
    legit() {
      if (!this.spotting()) return false;
      const P = this.P;
      this.ev.push(['l']);
      Game.progress.answer(this.spotKey(), 'All correct');
      if (P.answer.kind === 'none') { this.foundIt(false); this.sfx('ding', { gain: 0.6 }); this.save(); return true; }
      this.miss('Not all correct: one claim on this poster is wrong. Read each one again.');
      this.save();
      return true;
    }
    miss(text) {
      this.misses++; this.say = { text, at: Game.time }; this._sc = null;
      this.sfx('thunk', { gain: 0.4, rate: 0.9 });
      if (this.misses >= MAX_MISSES) {
        this.say = { text: this.P.answer.kind === 'none' ? "Everything on this poster checks out: it's all correct." : 'Dr. Voss circles it for you.', at: Game.time };
        this.foundIt(true);
      }
    }
    foundIt(revealed) {
      const a = this.P.answer;
      this.found = { kind: a.kind === 'none' ? 'legit' : a.kind, index: a.index };
      this.revealed = revealed; this.foundAt = Game.time; this.qAt = Game.time + 0.5;
      this.spotStar = !revealed && this.misses === 0 ? 1 : 0;
      if (!revealed) this.say = null;
      this.phase = 'q'; this._qc = null;
      if (a.kind !== 'none') this.sfx('scribble', { gain: 0.4 });
    }

    /* ---- what should it say? ---- */
    choose(i) {
      const q = this.inst;
      if (this.phase !== 'q' || !q || !(i >= 0 && i < q.options.length) || this.picks.includes(i)) return false;
      this.ev.push(['o', i]);
      if (!this.picks.length) Game.progress.answer(STEP + '/' + q.key, q.options[i]);   // first try only
      this.picks.push(i); this.pickedAt = Game.time; this._qc = null;
      if (Bank.check(q, i)) { this.sfx('ding', { gain: 0.6 }); this.toFix(this.spotStar, this.picks.length === 1 ? 1 : 0); }
      else this.sfx('thunk', { gain: 0.35, rate: 0.8 });
      this.save();
      return true;
    }
    toFix(spotStar, fixStar) {
      const P = this.P;
      this.phase = 'fix'; this.fixAt = Game.time; this._fc = null;
      this.results[this.bi] = { id: P.id, key: this.inst.key, cheat: P.cheat, title: P.title, legit: P.answer.kind === 'none', misses: this.misses,
        revealed: this.revealed, wrongPicks: this.picks.length - 1, spotStar, fixStar, stars: spotStar + fixStar };
      this.sfx('scribble', { gain: 0.45 });
    }
    next() {
      if (this.phase !== 'fix' || Game.time - this.fixAt < NEXT_GUARD) return false;
      if (this.bi < this.instances.length - 1) this.startPoster(this.bi + 1);
      else this.finish();
      return true;
    }
    finish() {
      const s = this.stars;
      this.phase = 'end'; this.endAt = Game.time; this.passed = s >= this.passStars;
      const S = Game.progress.bank && Game.progress.bank[STEP]; if (S) delete S.run;
      Bank.finish(STEP);                                                            // the next attempt draws new posters
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, s), last: s, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    /* a pass that leaves without "Collect your stamp" still keeps its stamp */
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    /* tests only: end the attempt now with the stars so far and stamp the step */
    done() {
      if (!this.instances) this.begin();
      if (this.phase !== 'end') this.finish();
      this.collected = true; Game.complete(STEP);
    }
    finishStub() { this.done(); }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', go: () => this.collect() }, { label: 'Try again (new posters)', go: () => this.begin(true) }]
        : [{ label: 'Try again (new posters)', go: () => this.begin(true) }, { label: 'Back to the map', go: () => this.back() }];
    }

    update() {
      if (this.phase === 'drop' && Game.time - this.boardAt > DROP + 0.1) this.phase = 'spot';
    }
    keyDown(k) {
      const n = parseInt(k, 10), kl = String(k).toLowerCase();
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.begin(); }
      else if (this.phase === 'drop' || this.phase === 'spot') {
        if (n === 0) this.legit();
        else if (['a', 'b', 'c', 'd'].includes(kl)) this.tapClaim('abcd'.indexOf(kl));
      }
      else if (this.phase === 'q') { if (n >= 1 && n <= 3) this.choose(n - 1); }
      else if (this.phase === 'fix') { if (k === 'Enter' || k === ' ') this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
    }

    /* ---------------- drawing ---------------- */
    lay(docked) {
      const mode = docked ? 'd' : UI.touch() ? 't' : 'm';
      const key = mode + '|' + Math.round(UI.tap(64)) + '|' + Math.round(UI.text(36)) + '|' + this.bi + '|' + this.P.id + '|' + (document.fonts ? document.fonts.status : '');
      let L = this.lays.get(key);
      if (!L) { const q = this.inst; L = posterLay(this.P, mode, q.options[q.answerIndex]); this.lays.set(key, L); }
      return L;
    }
    sub() {
      if (!this.instances || this.phase === 'intro' || this.phase === 'end') return '';
      return `poster ${this.bi + 1} of ${this.n} · ${this.stars} star${this.stars === 1 ? '' : 's'}`;
    }
    draw() {
      const docked = UI.docked(), Fa = F();
      Fa.backdrop();
      Fa.floor(950);
      UI.hud(TITLE, this.sub());
      if (this.phase === 'intro') return this.drawIntro(docked);
      if (this.phase === 'end') return this.drawEnd(docked);
      const L = this.lay(docked);
      // Dr. Voss beside the board when the panel leaves room (desktop / landscape; last frame's panel bottom)
      if (!docked) { const room = 1080 - (this.panelBottom || 1080) - 24; if (room >= 420) this.drawVoss(1720, 1080, Math.min(560, room)); }
      this.drawPoster(L, docked);
      if (docked) return this.drawDock();
      if (this.phase === 'drop' || this.phase === 'spot') this.drawSpot();
      else if (this.phase === 'q') this.drawQ();
      else this.drawFix();
    }

    /* the fix timeline (pure in T): strike each line (0.3 s), the rows below slide down (0.35 s), the rewrite is written */
    fixTimes(L) {
      const t0 = (this.fixAt || 0) + 0.2, nl = L.bad >= 0 ? L.rows[L.bad].lines.length : 0;
      const slide = t0 + nl * 0.3 + 0.05, write = slide + 0.3;
      return { t0, slide, write };
    }
    shiftK(L) {
      if (this.phase !== 'fix' || L.bad < 0) return 0;
      return E.inOutCubic(clamp((T - this.fixTimes(L).slide) / 0.35));
    }
    rowTop(L, r, k) { return r.top + (L.bad >= 0 && r.i > L.bad ? L.rw.h * k : 0); }

    /* ---- the tri-fold board on its table: flaps, the middle panel, the title band, the claims, every mark ---- */
    drawPoster(L, docked) {
      const Fa = F(), C = Fa.C, P = this.P, t0 = this.boardAt;
      const k = clamp((T - t0) / DROP), after = Math.max(0, T - t0 - DROP);
      const dy = -(1 - E.outCubic(k)) * 1150 + (k >= 1 ? -6 * Math.exp(-after * 5) * Math.abs(Math.sin(after * 9)) : 0);
      const spot = this.phase === 'spot' || this.phase === 'drop', live = !docked && this.phase === 'spot' && k >= 1;
      // the table stays put
      Fa.table(L.cx, L.bottom + 8, Math.min(1920, L.PW + 90), { seed: 31 });
      g.save(); g.translate(0, dy);
      this.drawBoard(L);
      this.drawClaims(L, docked, live, spot);
      if ((this.phase === 'q' || this.phase === 'fix') && this.found && this.found.kind !== 'legit') this.circleFound(L);
      if (this.phase === 'fix') this.drawMarks(L);
      g.restore();
    }
    drawBoard(L) {
      const Fa = F(), C = Fa.C, cy = L.PY + L.PH / 2, d = L.mode === 'd';
      // the side flaps (a touch darker, folded toward us), then the middle panel
      paperRect(Rn(L.PX + L.flap / 2 + 5), Rn(cy + 8), Rn(L.flap), Rn(L.PH - 28), FLAP, { seed: 701, depth: 1, boil: false });
      paperRect(Rn(L.PX + L.PW - L.flap / 2 - 5), Rn(cy + 8), Rn(L.flap), Rn(L.PH - 28), FLAP, { seed: 702, depth: 1, boil: false });
      paperRect(Rn(L.midL + L.midW / 2), Rn(cy), Rn(L.midW), Rn(L.PH), C.cream, { seed: 704 + (this.bi % 3), depth: 1.4, boil: false });
      if (IMG.tex_paper) texScrap('tex_paper', Rn(L.midL + L.midW / 2), Rn(cy), Rn(L.midW - 10), Rn(L.PH - 10), { seed: 707, depth: 0, torn: false, alpha: 0.22 });
      ink([[L.midL, L.PY + 14], [L.midL, L.bottom - 14]], { w: 2, color: 'rgba(80,60,40,0.35)', seed: 708, passes: 1 });
      ink([[L.midL + L.midW, L.PY + 14], [L.midL + L.midW, L.bottom - 14]], { w: 2, color: 'rgba(80,60,40,0.35)', seed: 709, passes: 1 });
      this.drawFlaps(L);
      // the title band
      paperRect(Rn(L.midL + L.midW / 2), Rn(L.bandY), Rn(L.midW - (d ? 60 : 44)), Rn(L.bandH), C.law, { seed: 711 + (this.bi % 4), depth: 0.8, boil: false });
      g.save(); Fa.fitFont(this.P.title, L.midW - (d ? 110 : 84), L.titleMax, SLAB); g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = C.cream;
      g.fillText(this.P.title, L.midL + L.midW / 2, L.bandY + 2); g.restore();
    }
    /* little flap decorations: a blue WHAT card + a photo on the left, an amber WHY card + a ribbon on the right */
    drawFlaps(L) {
      const C = F().C, f = L.flap, lx = L.PX + f / 2 + 5, rx = L.PX + L.PW - f / 2 - 5, top = L.PY + 40;
      const cw = Rn(f * 0.72), ch = Rn(f * 0.5), fs = Math.max(14, Math.round(f * 0.17));
      paperRect(Rn(lx), Rn(top + f * 0.4), cw, ch, C.law, { seed: 721, depth: 0.5, boil: false, rot: -0.06 });
      paperRect(Rn(rx), Rn(top + f * 0.4), cw, ch, C.amber, { seed: 722, depth: 0.5, boil: false, rot: 0.05 });
      g.save(); g.font = `${fs}px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillStyle = C.cream; g.fillText('WHAT', lx, top + f * 0.4 + 1); g.fillStyle = C.ink; g.fillText('WHY', rx, top + f * 0.4 + 2); g.restore();
      // a snapshot (kraft frame, a ruled doodle of rock layers)
      const py = top + f * 1.3, pw = Rn(f * 0.74), ph = Rn(f * 0.62);
      paperRect(Rn(lx), Rn(py), pw, ph, C.kraft, { seed: 723, depth: 0.6, boil: false, rot: 0.04 });
      for (let i = 0; i < 3; i++) ink([[lx - pw * 0.36, py - ph * 0.22 + i * ph * 0.2], [lx + pw * 0.36, py - ph * 0.18 + i * ph * 0.2]], { w: 3, color: C.amberDeep, seed: 724 + i, passes: 1 });
      // a graph doodle on the right
      paperRect(Rn(rx), Rn(py), pw, ph, C.graph, { seed: 727, depth: 0.6, boil: false, rot: -0.04 });
      ink([[rx - pw * 0.34, py + ph * 0.3], [rx + pw * 0.36, py + ph * 0.3]], { w: 2, color: C.ink, seed: 728, passes: 1 });
      ink([[rx - pw * 0.34, py + ph * 0.3], [rx - pw * 0.34, py - ph * 0.34]], { w: 2, color: C.ink, seed: 729, passes: 1 });
      ink([[rx - pw * 0.3, py + ph * 0.22], [rx + pw * 0.3, py - ph * 0.26]], { w: 3, color: C.red, seed: 730, passes: 1 });
      // a ribbon rosette low on the right flap, a thumbtacked note low on the left
      const ry = L.bottom - f * 1.0;
      if (ry > py + ph) {
        paperRect(Rn(rx - f * 0.1), Rn(ry + f * 0.34), Rn(f * 0.16), Rn(f * 0.44), C.law, { seed: 731, depth: 0.4, boil: false, rot: 0.2 });
        paperRect(Rn(rx + f * 0.1), Rn(ry + f * 0.34), Rn(f * 0.16), Rn(f * 0.44), C.law, { seed: 732, depth: 0.4, boil: false, rot: -0.2 });
        paperCircle(Rn(rx), Rn(ry), Rn(f * 0.24), C.law, { seed: 733, depth: 0.6 });
        paperCircle(Rn(rx), Rn(ry), Rn(f * 0.14), C.cream, { seed: 734, depth: 0 });
        paperRect(Rn(lx), Rn(ry), Rn(f * 0.66), Rn(f * 0.66), '#f7df8e', { seed: 735, depth: 0.5, boil: false, rot: -0.05 });
        for (let i = 0; i < 3; i++) ink([[lx - f * 0.22, ry - f * 0.12 + i * f * 0.13], [lx + f * 0.22, ry - f * 0.12 + i * f * 0.13]], { w: 2, color: 'rgba(60,50,40,0.5)', seed: 736 + i, passes: 1 });
      }
    }
    drawClaims(L, docked, live, spot) {
      const C = F().C, P = this.P, bi = this.bi, k = this.shiftK(L);
      L.rows.forEach(r => {
        const top = this.rowTop(L, r, k), cy = top + r.h / 2;
        if (live && !this.marks[r.i]) {
          const hit = UI._hit('po-claim' + bi + '_' + r.i, (L.colX + L.colR) / 2, cy, L.colW + 16, r.h);
          if (hit.hover) { g.save(); g.globalAlpha *= 0.28; g.fillStyle = '#f7df8e'; g.fillRect(L.colX - 8, top, L.colW + 16, r.h); g.restore(); }
          if (hit.clicked) this.tapClaim(r.i);
        }
        // the letter block (slab type on kraft)
        const bs = L.bw * 0.86, by = top + r.tpad + L.lh / 2;
        paperRect(Rn(L.colX + L.bw / 2), Rn(by), Rn(bs), Rn(bs), C.kraft, { seed: 750 + r.i, depth: 0.5, boil: false });
        g.save(); g.font = `${Math.round(bs * 0.62)}px ${SLAB}`; g.fillStyle = C.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(LETTER[r.i], L.colX + L.bw / 2, by + 1); g.restore();
        g.save(); g.font = L.font; g.fillStyle = C.ink; g.textAlign = 'left'; g.textBaseline = 'middle';
        r.lines.forEach((l, j) => g.fillText(l, L.textX, top + r.tpad + (j + 0.5) * L.lh));
        g.restore();
        const m = this.marks[r.i];
        if (m) {
          const a = clamp((T - m.at) / 0.25);
          g.save(); g.globalAlpha *= 0.5 * a; g.fillStyle = C.cream; g.fillRect(L.colX - 6, top + 2, L.colW + 12, r.h - 4); g.restore();
          tick(L.colX + L.bw / 2, by, L.fs / 40, PENCIL, m.at);
          hand('checks out', L.colR - 6, top + r.h - L.fs * 0.3, Math.round(L.fs * 0.85), m.at + 0.1, { align: 'right', color: PENCIL, rot: -0.04, seed: 755 + r.i, dur: 0.3 });
        }
      });
      void spot;
    }
    circleFound(L) {
      const C = F().C, f = this.found, pr = clamp((T - this.foundAt) / 0.45), r = L.rows[f.index];
      const top = this.rowTop(L, r, this.shiftK(L)), w = Math.max(...r.lineW) + L.bw + 20;
      loop(L.colX + w / 2 - 4, top + r.h / 2, Math.min(w / 2 + 20, L.colW / 2 + 22), r.lines.length * L.lh / 2 + L.pad * 0.7 + 6, { w: L.mode === 'd' ? 8 : 5, color: C.red, seed: 772, prog: pr });
    }
    /* Dr. Voss's red grease pencil: strike, then the right words written in; an all-correct poster gets ticks + a stamp */
    drawMarks(L) {
      const C = F().C, P = this.P, d = L.mode === 'd', tm = this.fixTimes(L);
      if (P.answer.kind === 'claim') {
        const r = L.rows[L.bad];
        r.lines.forEach((l, j) => {
          const x0 = L.textX - 8, x1 = x0 + r.lineW[j] + 16, y = r.top + r.tpad + (j + 0.5) * L.lh, a = tm.t0 + j * 0.3, pr = clamp((T - a) / 0.3);
          ink([[x0, y + 5], [x1, y - 5]], { w: d ? 8 : 6, color: C.red, seed: 780 + j, prog: pr, passes: 2, amp: 1.2 });
          if (pr > 0 && pr < 1) pencil(lerpN(x0, x1, pr), lerpN(y + 5, y - 5, pr), d ? 1.3 : 1);
        });
        // the rewrite, in the gap that opened under the struck claim
        if (T >= tm.write) {
          const rw = L.rw, y0 = r.top + r.h + Math.round(L.fs * 0.1);
          const cy0 = y0 + rw.lh / 2;
          ink([[L.textX - 30, cy0 + rw.fs * 0.3], [L.textX - 20, cy0 - rw.fs * 0.25], [L.textX - 10, cy0 + rw.fs * 0.3]], { w: d ? 6 : 4, color: C.red, seed: 786, prog: clamp((T - tm.write) / 0.2), passes: 1, step: 6 });
          rw.lines.forEach((l, j) => hand(l, L.textX, y0 + (j + 0.5) * rw.lh, rw.fs, tm.write + 0.1 + j * 0.4, { align: 'left', color: C.red, rot: -0.008, seed: 790 + j, dur: 0.4 }));
          // the grease pencil rides along the line being written
          const j = Math.floor((T - tm.write - 0.1) / 0.4);
          if (j >= 0 && j < rw.lines.length) {
            const e = clamp((T - tm.write - 0.1 - j * 0.4) / 0.4), w = measure(rw.lines[j], rw.font);
            pencil(L.textX + w * e, y0 + (j + 0.7) * rw.lh, d ? 1.3 : 1);
          }
        }
      } else {
        L.rows.forEach((r, k) => tick(L.colX + L.bw / 2, r.top + r.tpad + L.lh / 2, L.fs / 34, GREEN, tm.t0 + 0.2 + k * 0.15));
        const last = L.rows[L.rows.length - 1], below = last.top + last.h + (d ? 110 : 90), room = below + (d ? 70 : 60) <= L.colBot;
        const s = pop(T, tm.t0 + 0.6, 0.3) * (room ? (d ? 0.9 : 0.7) : (d ? 0.7 : 0.55));
        UI.stamp('ALL CORRECT', room ? (L.colX + L.colR) / 2 : L.colR - 200, room ? below : L.colBot - 50, { rot: -0.1, s, color: GREEN });
      }
    }

    /* ---------------- the right-hand panel (desktop / landscape) ---------------- */
    panelTop() { const hs = UI.hudSlot(0); return Math.max(150, hs.y + hs.r + 22); }
    card(x, y, w, h, seed) { paperRect(x + w / 2, y + h / 2, R10(w), R10(h), F().C.cream, { seed, depth: 1.3, boil: false }); }
    spotCard() {
      const key = this.bi + '|' + this.misses + '|' + (this.say ? this.say.text : '') + '|' + Math.round(UI.text(28)) + '|' + Math.round(UI.tap(84));
      if (this._sc && this._sc.key === key) return this._sc;
      const X = 1086, W = 1886 - X, top0 = this.panelTop();
      let S;
      for (const k of [1, 0.9, 0.8, 0.72, 0.64]) {
        const qs = Math.round(UI.text(40) * k), bs = Math.round(UI.text(30) * k), hs = Math.round(UI.text(28) * k), ns = Math.round(UI.text(30) * k);
        const qL = UI._wrap(`Poster ${this.bi + 1} of ${this.n}`, W - 60, `${qs}px ${SLAB}`);
        const body = UI._wrap('Tap the wrong claim. Nothing wrong? Call it All correct.', W - 60, `${bs}px ${SERIF}`);
        const bh = UI.tap(Math.round(84 * Math.max(k, 0.8)));
        const sayL = this.say ? UI._wrap(this.say.text, W - 60, `${ns}px ${HAND}`) : [];
        const h = 28 + qL.length * qs * 1.15 + 12 + body.length * bs * 1.3 + 24;
        const keyL = UI.keys ? UI._wrap('Keys: A–D pick a claim · 0 all correct', W - 40, `${hs}px ${HAND}`) : [];
        const total = h + 26 + bh + keyL.length * hs * 1.1 + 10 + (sayL.length ? 24 + sayL.length * ns * 1.15 + 30 : 0);
        S = { key, X, W, qs, bs, hs, ns, qL, body, bh, sayL, keyL, h, top: top0 };
        if (top0 + total <= 1070) break;
      }
      return (this._sc = S);
    }
    drawSpot() {
      const S = this.spotCard(), C = F().C, { X, W, qs, bs, hs, ns, qL, body, bh, sayL, keyL, h, top } = S;
      this.card(X, top, W, h, 820);
      let y = top + 28;
      text(qL, X + 30, y, qs * 1.15, `${qs}px ${SLAB}`, C.blueDeep); y += qL.length * qs * 1.15 + 12;
      text(body, X + 30, y, bs * 1.3, `${bs}px ${SERIF}`, C.ink);
      const by = top + h + 26 + bh / 2;
      if (this.t > 0.2 && UI.button('po-legit' + this.bi, 'All correct', X + W / 2, by, { w: Math.min(W - 50, 440), h: 84, color: GREEN, size: 44, font: HAND })) this.legit();
      let ky = by + bh / 2 + 6;
      keyL.forEach(l => { ky += hs * 1.1; hand(l, X + W / 2, ky - hs * 0.4, hs, this.boardAt + 0.5, { color: C.cream, seed: 829, dur: 0.01 }); });
      this.panelBottom = Math.max(ky + 10, by + bh / 2);
      if (sayL.length && this.say && this.phase === 'spot') {
        const ny = ky + 24, nh = sayL.length * ns * 1.15 + 30, sc = pop(T, this.say.at, 0.25, 1.6);
        if (sc > 0) { g.save(); g.translate(X + W / 2, ny + nh / 2); g.rotate(0.01); g.scale(sc, sc); paperRect(0, 0, R10(W), Math.round(nh / 6) * 6, '#fbf5e6', { seed: 831 + sayL.length, depth: 0.9, boil: false }); g.restore(); }
        sayL.forEach((l, j) => hand(l, X + 30, ny + 15 + (j + 0.5) * ns * 1.15, ns, this.say.at + 0.1 + j * 0.3, { align: 'left', color: C.red, seed: 832 + j, dur: 0.3 }));
        this.panelBottom = ny + nh;
      }
    }
    /* the question card: the options as answer cards (up to two lines); it moves left over the board when big type doesn't fit */
    qCard() {
      const key = this.bi + '|' + this.picks.length + '|' + Math.round(UI.text(28)) + '|' + Math.round(UI.tap(80));
      if (this._qc && this._qc.key === key) return this._qc;
      const q = this.inst, top0 = this.panelTop(), f = this.found;
      const intro = this.revealed ? (f.kind === 'legit' ? 'It was all correct.' : 'Dr. Voss circled it.') : f.kind === 'legit' ? 'Right: all correct!' : 'Got it: that claim is wrong.';
      const last = this.picks[this.picks.length - 1], why = last != null && last !== q.answerIndex ? Bank.why(q, last) : '';
      let R;
      outer: for (const X of [1086, 900, 720]) {
        for (const k of [1, 0.9, 0.8, 0.72]) {
          const W = 1886 - X;
          const is = Math.round(UI.text(32) * k), qs = Math.round(UI.text(34) * k), os = Math.round(UI.text(28) * k), ws = Math.round(UI.text(30) * k);
          const iL = UI._wrap(intro, W - 60, `${is}px ${HAND}`);
          const qL = UI._wrap(q.text, W - 60, `${qs}px ${SLAB}`);
          const opts = q.options.map(o => UI._wrap(o, W - 130, `${os}px ${SERIF}`));
          const rows = opts.map(ls => Math.max(UI.tap(80), ls.length * os * 1.22 + 26));
          const whyL = why ? UI._wrap(why, W - 60, `${ws}px ${HAND}`) : [];
          const h = 24 + iL.length * is * 1.15 + 8 + qL.length * qs * 1.18 + 14 + rows.reduce((a, b) => a + b, 0) + 14 + whyL.length * ws * 1.15 + (whyL.length ? 16 : 0);
          const top = Math.max(96, Math.min(top0, 1070 - h));
          R = { key, X, W, is, iL, qs, qL, os, opts, rows, ws, whyL, h, top, twoLines: opts.every(ls => ls.length <= 2) };
          if (top0 + h <= 1070 && R.twoLines) break outer;
        }
      }
      return (this._qc = R);
    }
    drawQ() {
      const R = this.qCard(), C = F().C, q = this.inst, t0 = this.qAt, { X, W, is, iL, qs, qL, os, opts, rows, ws, whyL, h, top } = R;
      if (T < t0) { this.panelBottom = 1080; return; }
      this.card(X, top, W, h, 850);
      let y = top + 24;
      iL.forEach((l, j) => hand(l, X + 30, y + (j + 0.5) * is * 1.15, is, t0, { align: 'left', color: this.found.kind === 'legit' ? GREEN : C.red, seed: 852 + j, dur: 0.3 }));
      y += iL.length * is * 1.15 + 8;
      text(qL, X + 30, y, qs * 1.18, `${qs}px ${SLAB}`, C.blueDeep); y += qL.length * qs * 1.18 + 14;
      opts.forEach((ls, i) => {
        const rh = rows[i], cy = y + rh / 2, crossed = this.picks.includes(i);
        const hit = crossed ? { hover: false, clicked: false } : UI._hit('po-opt' + this.bi + '_' + i, X + W / 2, cy, W - 30, rh - 6);
        paperRect(X + W / 2, cy, R10(W - 40), Math.round((rh - 10) / 2) * 2, hit.hover ? '#fffaf0' : crossed ? '#e6dcc6' : C.paper, { seed: 860 + i, depth: hit.hover ? 1.4 : 0.6, boil: false });
        g.save(); g.fillStyle = crossed ? PENCIL : C.blueDeep; g.fillRect(X + 32, cy - 22, 44, 44);
        g.font = `26px ${SLAB}`; g.fillStyle = C.cream; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), X + 54, cy + 1); g.restore();
        text(ls, X + 96, cy - ls.length * os * 1.22 / 2, os * 1.22, `${os}px ${SERIF}`, crossed ? PENCIL : C.ink);
        if (crossed) {
          const mw = Math.max(...ls.map(l => measure(l, `${os}px ${SERIF}`))), a = i === this.picks[this.picks.length - 1] ? this.pickedAt : -1e9;
          ink([[X + 90, cy + 10], [X + 104 + mw, cy - 10]], { w: 5, color: C.red, seed: 880 + i, prog: clamp((T - a) / 0.3), passes: 1 });
        }
        if (hit.clicked) this.choose(i);
        y += rh;
      });
      y += 14;
      whyL.forEach((l, j) => hand(l, X + 30, y + (j + 0.5) * ws * 1.15, ws, this.pickedAt + 0.2 + j * 0.4, { align: 'left', color: C.red, seed: 890 + j, dur: 0.4 }));
      this.panelBottom = X < 1086 ? 1080 : top + h;
    }
    /* Dr. Voss's card: the verdict, the kind of mistake, her reason, the stars */
    fixLines() {
      const P = this.P, r = this.results[this.bi] || { stars: 0, spotStar: 0, fixStar: 0 }, out = [], legit = P.answer.kind === 'none';
      out.push(['head', legit ? (r.spotStar ? 'All correct. Well judged!' : 'It was all correct.') : "Dr. Voss's red pencil"]);
      if (!legit) out.push(['rule', 'The mistake: ' + (SHORT[P.cheat] || P.cheat) + '.']);
      out.push(['note', 'Dr. Voss: ' + (P.fix || this.inst.explain || '')]);
      out.push(['stars', `+${r.stars} star${r.stars === 1 ? '' : 's'} (${legit ? 'all correct' : 'spot'} ${r.spotStar}, fix ${r.fixStar})`]);
      return out;
    }
    fixCard() {
      const key = this.bi + '|' + Math.round(UI.text(28)) + '|' + Math.round(UI.tap(92));
      if (this._fc && this._fc.key === key) return this._fc;
      const Fl = this.fixLines(), top0 = this.panelTop(), bh = UI.tap(92), X = 1086, W = 1886 - X;
      let R;
      for (const k of [1, 0.9, 0.8, 0.7, 0.62]) {
        const S = { head: [Math.round(UI.text(36) * k), SLAB], rule: [Math.round(UI.text(30) * k), SERIF], note: [Math.round(UI.text(34) * k), HAND], stars: [Math.round(UI.text(34) * k), HAND] };
        const rows = Fl.flatMap(([kk, t]) => UI._wrap(t, W - 64, `${S[kk][0]}px ${S[kk][1]}`).map((l, j) => [kk, l, j]));
        const h = rows.reduce((a, [kk]) => a + S[kk][0] * 1.24, 0) + 60;
        R = { key, X, W, rows, h, S, bh, top: top0 };
        if (top0 + h + 26 + bh + 14 <= 1080) break;
      }
      return (this._fc = R);
    }
    drawFix() {
      const R = this.fixCard(), C = F().C, { X, W, rows, h, S, bh, top } = R, t0 = this.fixAt + 0.2;
      this.card(X, top, W, h, 900);
      let y = top + 28, tt = t0, prev = null;
      const col = { head: this.P.answer.kind === 'none' ? GREEN : C.red, rule: C.ink, note: C.red, stars: GREEN };
      rows.forEach(([k, l]) => {
        const s = S[k][0], cy = y + s * 0.62;
        if (k !== prev) { y += prev ? s * 0.12 : 0; prev = k; }
        if (k === 'stars' || k === 'note') hand(l, X + 32, y + s * 0.62, s, tt, { align: 'left', color: col[k], seed: 910 + Math.round(y) % 50, dur: 0.35 });
        else if (T >= tt) { g.save(); g.font = `${s}px ${S[k][1]}`; g.fillStyle = col[k]; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(l, X + 32, cy); g.restore(); }
        tt += 0.12; y += s * 1.24;
      });
      const label = this.bi < this.n - 1 ? 'Next poster' : 'See my stars';
      this.panelBottom = top + h + 26 + bh;
      if (Game.time - this.fixAt > NEXT_GUARD && UI.button('po-next', label, X + W / 2, top + h + 26 + bh / 2, { w: 420, h: 92, color: C.blueDeep, size: 46, font: HAND })) this.next();
    }

    /* ---------------- portrait: the poster is the picture; everything to tap is in the dock ---------------- */
    drawDock() {
      const P = this.P, q = this.inst, id = this.attempt + '_' + this.bi;
      const claimOpts = P.claims.map((c, i) => `${LETTER[i]}. ${c.text}`);
      if (this.phase === 'drop' || this.phase === 'spot') {
        Dock.text('po-how', [`Poster ${this.bi + 1} of ${this.n}: tap the wrong claim. Nothing wrong? All correct.`], { cls: 'note' });
        if (this.phase !== 'spot') return;
        Dock.choices('po-claims' + id, { prompt: titleCase(P.title), options: claimOpts, state: P.claims.map((c, i) => (this.marks[i] ? 'faded' : '')),
          notes: P.claims.map((c, i) => (this.marks[i] ? 'Checks out. ' + checksOut(c) : '')), onChoose: i => this.tapClaim(i) });
        Dock.buttons('po-legit' + id, [{ label: 'All correct', wide: true, onClick: () => this.legit() }]);
        if (this.say) Dock.text('po-say' + id + '_' + this.misses, [this.say.text], { cls: 'wrong', reveal: true });
        return;
      }
      const f = this.found;
      const foundTxt = f.kind === 'legit' ? (this.revealed ? 'It was all correct.' : 'Right: all correct!')
        : (this.revealed ? 'Dr. Voss circled: ' : 'You circled: ') + claimOpts[f.index];
      if (this.phase === 'q') {
        Dock.text('po-found' + id, [foundTxt], { cls: this.revealed ? 'note' : 'right' });
        Dock.choices('po-q' + id, { prompt: q.text, options: q.options, state: q.options.map((o, i) => (this.picks.includes(i) ? 'wrong' : '')),
          notes: q.options.map((o, i) => (this.picks.includes(i) ? Bank.why(q, i) : '')), onChoose: i => this.choose(i) });
        return;
      }
      const Fl = this.fixLines();
      Dock.choices('po-q' + id, { prompt: q.text, options: q.options, state: q.options.map((o, i) => (i === q.answerIndex ? 'right' : this.picks.includes(i) ? 'wrong' : 'faded')), locked: true });
      Dock.heading('po-fixh' + id, Fl[0][1]);
      Dock.text('po-fixt' + id, Fl.filter(([k]) => k === 'rule' || k === 'note').map(([, t]) => t), { cls: 'note' });
      Dock.text('po-stars' + id, Fl.filter(([k]) => k === 'stars').map(([, t]) => t), { cls: 'right', reveal: true });
      Dock.button({ id: 'next', glyph: this.bi < this.n - 1 ? 'Next poster' : 'See my stars', label: 'next', aria: 'next poster', go: true, onClick: () => this.next() });
    }

    introLines() {
      return ['The science fair opens soon, and six posters still need judging. Dr. Voss hands you her red grease pencil.',
        'Most posters have one wrong claim about laws, theories or hypotheses.',
        'Tap it, then pick what it should say. Nothing wrong? Call it All correct.', `${this.n} posters. ${this.passStars} of ${this.of} stars for the stamp.`];
    }
    drawVoss(x, y, h) {
      const rise = E.outBack(P(T, this.enteredAt + 0.2, this.enteredAt + 0.7), 1.4);
      Puppet.draw('voss', x, y + (1 - rise) * 500, h, { facing: -1, t: T, env: 0, pose: this.phase === 'fix' || this.phase === 'end' ? 'point' : undefined });
    }
    banner(word, x, y, size, seed, color) {
      const C = F().C;
      g.save(); g.font = `${size}px ${SLAB}`; const w = g.measureText(word).width; g.restore();
      paperRect(Rn(x), Rn(y), R10(w + size * 1.2), Rn(size * 1.6), color || C.red, { seed, depth: 1, boil: false, rot: -0.02 });
      g.save(); g.font = `${size}px ${SLAB}`; g.fillStyle = C.cream; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(word, x, y + size * 0.06); g.restore();
    }
    drawIntro(docked) {
      const t0 = this.enteredAt, C = F().C, lines = this.introLines();
      this.drawVoss(docked ? 1600 : 1660, 1080, docked ? 760 : 800);
      if (docked) {
        this.banner('FIX THE POSTER', 760, 420, 96, 601);
        Dock.heading('po-intro', TITLE, { cls: 'big' });
        Dock.text('po-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start', label: 'start', aria: 'Start judging posters', go: true, color: 'red', onClick: () => this.begin() });
        return;
      }
      this.banner('FIX THE POSTER', 740, 270, 70, 601);
      // the text shrinks until the card and the Start button fit (a phone's UI.text scale makes it wrap more)
      const W = 1040, top = 380, bh = UI.tap(110), cx = 740;
      let fs = Math.round(UI.text(36)), wl, nL, h;
      for (;;) {
        wl = lines.map((l, i) => UI._wrap(l, W - 70, `${i === 3 ? 'bold ' : ''}${fs}px ${SERIF}`));
        nL = wl.reduce((a, l) => a + l.length, 0); h = nL * fs * 1.42 + 60;
        if (top + h + 24 + bh <= 1070 || fs <= 24) break;
        fs -= 2;
      }
      this.card(cx - W / 2, top, W, h, 935);
      let y = top + 30 + fs * 0.71;
      wl.forEach((ls, i) => ls.forEach(tl => {
        if (T >= t0 + 0.5 + i * 0.3) { g.save(); g.font = `${i === 3 ? 'bold ' : ''}${fs}px ${SERIF}`; g.fillStyle = i === 3 ? C.red : C.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.globalAlpha = clamp((T - t0 - 0.5 - i * 0.3) / 0.3); g.fillText(tl, cx, y); g.restore(); }
        y += fs * 1.42;
      }));
      const by = Math.min(1080 - bh / 2 - 10, top + h + 24 + bh / 2);
      if (this.t > 0.8 && UI.button('po-start', 'Start', cx, by, { w: 420, h: 110, color: C.red, size: 56, font: HAND })) this.begin();
      if (this.t > 1.2 && UI.keys) hand(UI.hint('(or press Enter)', ''), cx + 240, by, UI.text(28), t0 + 1.2, { align: 'left', color: C.cream, seed: 603 });
    }
    drawEnd(docked) {
      const s = this.stars, of = this.of, btns = this.endButtons(), C = F().C;
      const rows = this.instances.map((inst, i) => { const r = this.results[i] || { stars: 0 }; return { r, text: `${titleCase(inst.item.title)}: ${SHORT[inst.item.cheat] || inst.item.cheat}` }; });
      const word = this.passed ? 'JUDGED' : 'TRY AGAIN';
      if (docked) {
        this.banner(`${s} / ${of}`, 700, 380, 130, 950, C.blueDeep);
        UI.stamp(word, 700, 720, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.5, color: C.red });
        this.drawVoss(1620, 1080, 700);
        Dock.heading('po-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('po-end', [this.passed ? 'Judged! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp. New posters?`]);
        Dock.list('po-end', rows.map((x, i) => ({ badge: String(i + 1), color: x.r.stars === 2 ? GREEN : C.red, lines: [x.text, `${x.r.stars} of 2 stars`] })));
        Dock.text('po-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('po-endb', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      this.banner(`${s} / ${of}`, 700, 250, 96, 950, C.blueDeep);
      UI.stamp(word, 1320, 250, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.1, color: C.red });
      const fs = Math.round(UI.text(30)), rh = Math.max(62, fs * 1.5), bh = UI.tap(100), ch = (rows.length + 1) * rh + 50;
      const cardTop = Math.min(350, 1080 - 40 - bh - 36 - ch), X0 = 220, W = 1480;
      this.card(X0, cardTop, W, ch, 960);
      rows.forEach((x, i) => {
        const y = cardTop + 25 + (i + 0.5) * rh;
        let t = `${i + 1}. ${x.text}`;
        g.save(); g.font = `${fs}px ${SERIF}`;
        if (g.measureText(t).width > W - 330) { while (g.measureText(t + '…').width > W - 330 && t.length > 8) t = t.slice(0, -1); t = t.trimEnd() + '…'; }
        g.fillStyle = x.r.legit ? GREEN : C.ink; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(t, X0 + 50, y); g.restore();
        hand(`${x.r.stars} of 2`, X0 + W - 60, y, fs * 1.2, this.endAt + 0.4 + i * 0.1, { align: 'right', color: x.r.stars === 2 ? GREEN : C.red, seed: 970 + i });
      });
      const py = cardTop + 25 + (rows.length + 0.5) * rh, by = Math.min(1080 - 40 - bh / 2, cardTop + ch + 36 + bh / 2);
      hand(this.passed ? 'Judged! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp. New posters?`, X0 + W / 2, py, Math.round(UI.text(40)), this.endAt + 0.8, { color: C.red, seed: 980, dur: 0.5 });
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('po-end' + i, b.label, 700 + i * 520, by, { w: 480, h: 100, color: i === 0 ? C.blueDeep : C.amberDeep, size: 44, font: HAND })) b.go(); });
      hand(`quiz code ${this.seed}`, 1880, by + bh / 2 + 10 > 1070 ? by - bh / 2 - 20 : by + bh / 2 + 10, 28, this.endAt + 0.6, { align: 'right', color: C.cream, seed: 985 });
    }
  }

  /* small drawing helpers */
  function text(lines, x, yTop, lh, font, color) {
    g.save(); g.font = font; g.fillStyle = color; g.textAlign = 'left'; g.textBaseline = 'middle';
    lines.forEach((l, j) => g.fillText(l, x, yTop + (j + 0.5) * lh));
    g.restore();
  }
  function loop(cx, cy, rx, ry, { w = 5, color, seed = 1, prog = 1 } = {}) {
    const pts = [], n = 64;
    for (let i = 0; i <= n * 1.08; i++) { const a = i / n * TAU - 2.2, c = Math.cos(a), s = Math.sin(a); pts.push([cx + rx * Math.sign(c) * Math.pow(Math.abs(c), 0.45) * (1 + 0.02 * Math.sin(i * 0.7)), cy + ry * Math.sign(s) * Math.pow(Math.abs(s), 0.6)]); }
    ink(pts, { w, color, seed, prog, passes: 1, step: 12, amp: 1.4 });
  }
  function tick(x, y, s, color, t0) {
    const pr = clamp((T - t0) / 0.3);
    if (pr <= 0) return;
    ink([[x - 18 * s, y], [x - 4 * s, y + 16 * s], [x + 24 * s, y - 22 * s]], { w: Math.max(4, 6 * s), color, seed: Math.round(x + y) % 97 + 1, prog: pr, passes: 1, step: 6 });
  }
  /* Dr. Voss's red grease pencil (its tip at x, y), drawn with plain fills: cheap, no shadows */
  function pencil(x, y, s = 1) {
    g.save(); g.translate(x, y); g.rotate(-0.75); g.scale(s, s);
    g.fillStyle = '#8e1f14'; g.beginPath(); g.moveTo(0, 0); g.lineTo(10, -7); g.lineTo(10, 7); g.closePath(); g.fill();
    g.fillStyle = '#c8321f'; g.fillRect(10, -9, 26, 18);
    g.fillStyle = '#efe4c8'; g.fillRect(36, -11, 124, 22);
    g.fillStyle = 'rgba(0,0,0,0.12)'; g.fillRect(36, 3, 124, 8);
    g.strokeStyle = 'rgba(90,70,40,0.5)'; g.lineWidth = 1.5; for (let i = 0; i < 5; i++) { g.beginPath(); g.moveTo(50 + i * 22, -11); g.lineTo(60 + i * 22, 11); g.stroke(); }
    g.fillStyle = '#c8321f'; g.fillRect(160, -11, 18, 22);
    g.restore();
  }

  Poster.STEP = STEP; Poster.pool = pool; Poster.layout = posterLay; Poster.checksOut = checksOut;
  Object.defineProperty(Poster, 'solution', { get() { const s = Game.current(); return s instanceof Poster ? s.solution : null; } });
  /* the live scene's tap rects on the canvas (tests click them): the claims, centred */
  Poster.zones = () => {
    const s = Game.current();
    if (!(s instanceof Poster) || !s.P || s.phase === 'intro' || s.phase === 'end') return null;
    const L = s.lay(UI.docked()), k = s.shiftK(L);
    return { claims: L.rows.map(r => ({ i: r.i, x: (L.colX + L.colR) / 2, y: s.rowTop(L, r, k) + r.h / 2, w: L.colW, h: r.h })) };
  };
  /* the current layout's overflow report (tests): claim lines wider than the column, the rewrite, the bottom */
  Poster.fit = () => {
    const s = Game.current();
    if (!(s instanceof Poster) || !s.P) return null;
    const L = s.lay(UI.docked()), last = L.rows[L.rows.length - 1];
    const rwOver = L.rw.lines.map(l => measure(l, L.rw.font)).filter(w => w > L.textW + 0.5);
    return { mode: L.mode, fs: L.fs, over: L.over, lineOver: L.rows.flatMap(r => r.lineW.filter(w => w > L.textW + 0.5)), rwOver, rwLines: L.rw.lines.length,
      claimLines: L.rows.map(r => r.lines.length), bottom: last.top + last.h + L.rw.h, colBot: L.colBot };
  };
  /* tests / screenshots: swap the current poster for a pool item (by id), back to the spot phase */
  Poster.preview = id => {
    const s = Game.current();
    if (!(s instanceof Poster) || !s.instances) return false;
    const it = pool().items.find(x => x.id === id);
    if (!it) return false;
    const inst = Bank.draw({ ...pool(), items: [it], draw: { ...pool().draw, n: 1, require: [], unique: null } }, { seed: 7 })[0];
    s.instances[s.bi] = inst;
    s.setup(s.bi); s.boardAt = Game.time - DROP - 2; s.phase = 'spot';
    return true;
  };
  Levels.poster = Poster;
})();
